import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import ChessBoardView from '@/components/chess/ChessBoardView';
import { playChessMoveSound, playChessCaptureSound } from '@/lib/chessSounds';
import { CheckCircle2, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

// FASE QUIZZES · PASO 4B+4C — Runner de UNA actividad (tipo puzzle).
// Fuente: activity_snapshots[position] (Paso 1B). NO consulta el Study.
// Reutiliza ChessBoardView + chess.js + los mismos handlers de click-to-move
// y drag&drop que LivePuzzle/PlayComputerPlayer.
//
// PASO 4C — intentos + scoring:
// · Max 3 intentos por ACTIVIDAD (no por movimiento).
// · Puntuación proporcional: attempt 1 → maxPoints · 2 → 2/3 · 3 → 1/3 · fail → 0.
// · Movimiento incorrecto = intento fallido → reset a posición inicial (o
//   agotar intentos → completed con 0 pts).
// · Callback onCompleted disparado UNA sola vez por actividad.

function fmtTime(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2,'0')}:${String(s % 60).padStart(2,'0')}`;
}

// Scoring: attempt(1..N) & success. Exacta para maxPoints=30 → 30/20/10/0.
function computePoints(attempt, maxPoints, maxAttempts) {
  const mp = Math.max(0, Math.round(maxPoints || 30));
  const ma = Math.max(1, Math.round(maxAttempts || 3));
  if (attempt < 1 || attempt > ma) return 0;
  // proporcional lineal: attempt 1 → mp, attempt 2 → mp * (ma-1)/ma, ...
  return Math.round(mp * (ma - (attempt - 1)) / ma);
}

const WRONG_FEEDBACK = [
  '💡 ¡Casi! Observa nuevamente la posición.',
  '💪 ¡Tú puedes! Mira con atención y vuelve a intentarlo.',
  '✨ ¡Vamos con el siguiente reto!',
];

export default function QuizPuzzleRunner({
  activity, position, totalActivities, quizTitle,
  maxPoints = 30, maxAttempts = 3,
  initialAttemptsUsed = 0,
  resumeFen = "",
  resumeMoveHistory = [],
  onCompleted, onAttemptFailed, onProgress,
}) {
  const gameRef = useRef(null);
  const [fen, setFen] = useState(activity?.fen || '');
  const [orientation, setOrientation] = useState('white');
  const [solIndex, setSolIndex] = useState(0);
  const [attemptsUsed, setAttemptsUsed] = useState(0);   // intentos consumidos (0..maxAttempts)
  const [locked, setLocked] = useState(false);           // true una vez completed (success o fail)
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalDests, setLegalDests] = useState({});
  const [wrongFlash, setWrongFlash] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const startedRef = useRef(Date.now());
  const completedFiredRef = useRef(false);
  const movesPlayedRef = useRef([]);
  // Layout-only: ancho del tablero medido del contenedor real (ResizeObserver),
  // evita overflow en tablet (768px) donde el sidebar reduce el ancho útil.
  const boardWrapRef = useRef(null);
  const [boardPx, setBoardPx] = useState(480);
  useEffect(() => {
    const el = boardWrapRef.current;
    if (!el) return;
    const measure = () => { const w = el.clientWidth || 0; if (w > 0) setBoardPx(Math.max(200, Math.min(560, Math.floor(w)))); };
    measure();
    let ro; if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  // Reset al montar / cambiar de actividad.
  useEffect(() => {
    if (!activity) return;
    try {
      const startFen = resumeFen || activity.fen;
      const c = new Chess(startFen);
      gameRef.current = c;
      setFen(c.fen());
      const restoredHistory = Array.isArray(resumeMoveHistory) ? [...resumeMoveHistory] : [];
      movesPlayedRef.current = restoredHistory;
      setSolIndex(restoredHistory.length);
      setAttemptsUsed(Math.max(0, Math.min(maxAttempts - 1, initialAttemptsUsed || 0)));
      setLocked(false);
      setSelectedSquare(null);
      setLegalDests({});
      setOrientation(c.turn() === 'w' ? 'white' : 'black');
      startedRef.current = Date.now();
      setSeconds(0);
      completedFiredRef.current = false;
    } catch {
      gameRef.current = null;
    }
  }, [activity?.id, activity?.source_activity_id, resumeFen]); // eslint-disable-line

  // Timer informativo.
  useEffect(() => {
    const iv = setInterval(() => setSeconds(Math.floor((Date.now() - startedRef.current) / 1000)), 500);
    return () => clearInterval(iv);
  }, []);

  const solutionMoves = useMemo(() => (activity?.solution_moves || []).map(s => String(s).trim()).filter(Boolean), [activity]);
  const solLen = solutionMoves.length;
  // F1 · líneas de solución aceptadas (múltiples respuestas). Fallback a la
  // línea única legacy si no hay accepted_solutions.
  const solutionLines = useMemo(() => {
    const acc = activity?.accepted_solutions;
    if (Array.isArray(acc) && acc.length) {
      const norm = acc.map(ln => (ln || []).map(s => String(s).trim()).filter(Boolean)).filter(ln => ln.length);
      if (norm.length) return norm;
    }
    return solutionMoves.length ? [solutionMoves] : [];
  }, [activity, solutionMoves]);

  // Reset SÓLO el tablero (misma actividad) para un nuevo intento.
  const resetBoardForRetry = useCallback(() => {
    if (!activity) return;
    try {
      const c = new Chess(activity.fen);
      gameRef.current = c;
      setFen(c.fen());
      setSolIndex(0);
      setSelectedSquare(null);
      setLegalDests({});
      movesPlayedRef.current = [];
    } catch { /* ignore */ }
  }, [activity]);

  // Fire callback una única vez.
  const finish = useCallback((success, attemptsAtFinish) => {
    if (completedFiredRef.current) return;
    completedFiredRef.current = true;
    setLocked(true);
    const points = success ? computePoints(attemptsAtFinish, maxPoints, maxAttempts) : 0;
    try {
      onCompleted?.({
        activity_id: activity?.source_activity_id || activity?.id,
        position,
        attempts_used: attemptsAtFinish,
        points_earned: points,
        elapsed_seconds: Math.floor((Date.now() - startedRef.current) / 1000),
        success,
        completed: true,
      });
    } catch { /* stub 4D — no bloquear si el padre falla */ }
  }, [activity, position, onCompleted, maxPoints, maxAttempts]);

  // Registrar un intento fallido. Reset del tablero, feedback positivo, o
  // termina si ya se agotaron los intentos.
  const registerFailedAttempt = useCallback(() => {
    setAttemptsUsed(prev => {
      const next = prev + 1;
      const msg = WRONG_FEEDBACK[Math.min(next, WRONG_FEEDBACK.length) - 1];
      if (next >= maxAttempts) {
        toast(WRONG_FEEDBACK[WRONG_FEEDBACK.length - 1], { duration: 1400 });
        setTimeout(() => finish(false, next), 450);
      } else {
        toast(msg, { duration: 1400 });
        // Paso 4D — persistir intento parcial (no completed) para poder reanudar.
        try {
          onAttemptFailed?.({
            activity_id: activity?.source_activity_id || activity?.id,
            position,
            attempts_used: next,
            completed: false,
            elapsed_seconds: Math.floor((Date.now() - startedRef.current) / 1000),
            current_fen: activity?.fen || '',
            move_history: [],
          });
        } catch { /* silent */ }
        setTimeout(() => resetBoardForRetry(), 350);
      }
      return next;
    });
  }, [maxAttempts, finish, resetBoardForRetry, onAttemptFailed, activity, position]);

  const tryMove = useCallback((from, to) => {
    const c = gameRef.current;
    if (!c || locked || solutionLines.length === 0) return false;
    const tmp = new Chess(c.fen());
    let move; try { move = tmp.move({ from, to, promotion: 'q' }); } catch { return false; }
    if (!move) return false;

    // F1 · múltiples respuestas correctas — branch-aware sobre las líneas jugadas.
    const norm = (s) => (s || '').replace(/[+#!?]/g, '').trim();
    const played = movesPlayedRef.current;
    const i = played.length;
    const viable = solutionLines.filter(ln => ln.length > i && ln.slice(0, i).every((s, k) => norm(s) === norm(played[k])));
    const matchLine = viable.find(ln => norm(ln[i]) === norm(move.san));
    if (!matchLine) {
      // Intento fallido para esta actividad. Flash rojo breve + reset.
      setWrongFlash(true); setTimeout(() => setWrongFlash(false), 400);
      registerFailedAttempt();
      return false;
    }
    // Aceptado — aplicar en el estado real
    c.move({ from, to, promotion: move.promotion || 'q' });
    movesPlayedRef.current.push(move.san);
    setFen(c.fen());
    try {
      onProgress?.({
        activity_id: activity?.source_activity_id || activity?.id,
        position,
        attempts_used: attemptsUsed,
        completed: false,
        elapsed_seconds: Math.floor((Date.now() - startedRef.current) / 1000),
        current_fen: c.fen(),
        move_history: [...movesPlayedRef.current],
      });
    } catch { /* silent */ }
    setSelectedSquare(null); setLegalDests({});
    (move.captured ? playChessCaptureSound : playChessMoveSound)();

    const afterStudent = movesPlayedRef.current;
    const cont = solutionLines.filter(ln => ln.length > afterStudent.length && ln.slice(0, afterStudent.length).every((s, k) => norm(s) === norm(afterStudent[k])));
    if (cont.length) {
      // Auto-jugar la respuesta del oponente de la línea elegida
      const oppSan = cont[0][afterStudent.length];
      setTimeout(() => {
        const cc = gameRef.current;
        let opp; try { opp = cc.move(oppSan); } catch { opp = null; }
        if (opp) {
          movesPlayedRef.current.push(oppSan);
          setFen(cc.fen());
          try {
            onProgress?.({
              activity_id: activity?.source_activity_id || activity?.id,
              position,
              attempts_used: attemptsUsed,
              completed: false,
              elapsed_seconds: Math.floor((Date.now() - startedRef.current) / 1000),
              current_fen: cc.fen(),
              move_history: [...movesPlayedRef.current],
            });
          } catch { /* persistencia parcial silenciosa */ }
          (opp.captured ? playChessCaptureSound : playChessMoveSound)();
        }
        const now = movesPlayedRef.current;
        const stillCont = solutionLines.some(ln => ln.length > now.length && ln.slice(0, now.length).every((s, k) => norm(s) === norm(now[k])));
        setSolIndex(now.length);
        if (!stillCont) finish(true, attemptsUsed + 1);
      }, 380);
    } else {
      setSolIndex(afterStudent.length);
      finish(true, attemptsUsed + 1);
    }
    return true;
  }, [locked, solutionLines, registerFailedAttempt, finish, attemptsUsed, onProgress, activity, position]);

  // Click-to-move (patrón LivePuzzle/PlayComputerPlayer)
  const computeLegalDests = useCallback((sq) => {
    const c = gameRef.current;
    if (!c) return {};
    try {
      const mv = c.moves({ square: sq, verbose: true });
      const d = {};
      mv.forEach(m => { d[m.to] = { isCapture: !!(m.flags && (m.flags.includes('c') || m.flags.includes('e'))) }; });
      return d;
    } catch { return {}; }
  }, []);

  const handleSquareClick = useCallback((sq) => {
    const c = gameRef.current;
    if (!c || locked) return;
    if (selectedSquare) {
      if (sq === selectedSquare) { setSelectedSquare(null); setLegalDests({}); return; }
      if (legalDests[sq]) { tryMove(selectedSquare, sq); return; }
      const p = c.get(sq);
      if (p && p.color === c.turn()) { setSelectedSquare(sq); setLegalDests(computeLegalDests(sq)); return; }
      setSelectedSquare(null); setLegalDests({}); return;
    }
    const p = c.get(sq);
    if (!p || p.color !== c.turn()) return;
    const d = computeLegalDests(sq);
    if (Object.keys(d).length === 0) return;
    setSelectedSquare(sq); setLegalDests(d);
  }, [computeLegalDests, legalDests, selectedSquare, locked, tryMove]);

  const hintStyles = useMemo(() => {
    const s = {};
    if (selectedSquare) s[selectedSquare] = { backgroundColor: 'rgba(255, 236, 100, 0.75)' };
    Object.entries(legalDests).forEach(([dst, meta]) => {
      const base = s[dst] || {};
      s[dst] = {
        ...base,
        background: meta.isCapture
          ? 'radial-gradient(circle, transparent 0 88%, rgba(0,0,0,0.42) 88% 96%, transparent 96% 100%)'
          : 'radial-gradient(circle, rgba(0,0,0,0.28) 0 18%, transparent 18% 100%)',
      };
    });
    return s;
  }, [selectedSquare, legalDests]);

  const pct = totalActivities > 0 ? Math.round(((position) / totalActivities) * 100) : 0;
  const instruction = (() => {
    const title = (activity?.title || '').trim();
    return !title || title === '?' ? 'Encuentra la mejor jugada.' : title;
  })();
  const attemptForBadge = Math.min(maxAttempts, attemptsUsed + (locked ? 0 : 1));

  if (!activity) return null;

  return (
    <div data-testid="quiz-runner" className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-indigo-50/40 via-white to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-4">
          <div data-testid="quiz-runner-title" className="font-display font-black text-lg sm:text-xl text-slate-900 truncate">🎯 {quizTitle}</div>
          <div className="flex items-center justify-between mt-1 gap-3">
            <div data-testid="quiz-runner-progress-text" className="text-xs sm:text-sm text-slate-600 font-semibold">Actividad {position + 1} de {totalActivities}</div>
            <div data-testid="quiz-runner-timer" className="inline-flex items-center gap-1 text-xs sm:text-sm font-mono text-slate-500">
              <Clock className="w-3.5 h-3.5"/> {fmtTime(seconds)}
            </div>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden mt-2">
            <div data-testid="quiz-runner-progress-bar" className="h-full bg-gradient-to-r from-indigo-500 to-fuchsia-500 transition-all" style={{ width: `${pct}%` }}/>
          </div>
        </div>

        {/* Instrucción */}
        <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-indigo-600 font-bold flex items-center gap-1"><Sparkles className="w-3 h-3"/> Instrucción</div>
              <div data-testid="quiz-runner-instruction" className="font-display font-extrabold text-sm sm:text-base text-slate-900 mt-0.5">{instruction}</div>
            </div>
            <div data-testid="quiz-runner-attempt-badge" className="text-[11px] text-slate-500 font-semibold whitespace-nowrap ml-3">
              Intento {attemptForBadge} de {maxAttempts}
            </div>
          </div>
        </div>

        {/* Tablero */}
        <div className={`rounded-2xl bg-white border p-3 sm:p-4 transition-colors ${wrongFlash ? 'border-rose-400' : 'border-slate-200'}`}>
          <div ref={boardWrapRef} data-testid="quiz-runner-board" className="w-full max-w-[400px] mx-auto">
            <ChessBoardView
              fen={fen}
              onPieceDrop={tryMove}
              onSquareClick={handleSquareClick}
              extraSquareStyles={hintStyles}
              orientation={orientation}
              boardWidth={boardPx}
              arePiecesDraggable={!locked}
            />
          </div>
        </div>

        {/* Estado temporal — el avance lo controla el padre via onCompleted */}
        {locked && (
          <div data-testid="quiz-runner-completed" className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 shrink-0"/>
            <div>
              <div className="font-display font-extrabold">Actividad completada</div>
              <div className="text-xs text-emerald-700">Preparando el siguiente reto…</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
